import { Box, Stack, Typography, useTheme } from '@mui/material';
import { APPROVAL_FLOW_INVOICE_TYPES, type ApprovalFlowInvoiceTypes } from '@weft/api-definition';
import { isNotNullOrUndefined, uniqueBy } from '@weft/utils';
import { ToggleChip } from 'apps/app/src/components/new-design/chip/ToggleChip';
import { SearchInput } from 'apps/app/src/components/new-design/table/vy-table-toolbar/vy-table-toolbar-components/SearchInput';
import { useEffectOnce } from 'apps/app/src/hooks/use-effect-once';
import { useStateParams } from 'apps/app/src/hooks/use-state-params';
import { deserializeEnumArrayParam, serializeStringArrayParam } from 'apps/app/src/utils/params-formatting';
import React from 'react';

type ApprovalFlowInvoicesListHeaderProps = {
  onSearchChange?: (search: string) => void;
  onFiltersChange?: (filters: ApprovalFlowInvoiceTypes[]) => void;
};

const ApprovalFlowInvoicesListHeader: React.FC<ApprovalFlowInvoicesListHeaderProps> = ({ onFiltersChange, onSearchChange }) => {
  const [filters, setFilters] = useSelectedFilters();
  const vyTheme = useTheme();

  useEffectOnce(() => {
    if (isNotNullOrUndefined(filters)) {
      onFiltersChange?.(filters);
    }
  });
  
  const handleFilterChange = (selectedFilter: ApprovalFlowInvoiceTypes, isToggled: boolean) => {
    const newFilters = isToggled 
      ? uniqueBy([...filters ?? [], selectedFilter], (filter) => filter) 
      : filters?.filter((filter) => filter !== selectedFilter) ?? [];
    onFiltersChange?.(newFilters);
    setFilters(newFilters);
  };

  const handleSearchChange = (search: string) => {
    onSearchChange?.(search);
  };

  return (
    <Stack
      padding={1}
      paddingX={2}
      direction="column"
    >
      <Stack
        paddingY={2}
        direction="row"
      >
        <Typography variant='h6'>Invoices</Typography>
      </Stack>
      <Stack direction="row">
        <SearchInput
          placeholder='Search'
          onChange={(event) => handleSearchChange(event.target.value)}
        />
      </Stack>
      <Box paddingY={2}>
        <Typography variant='subtitle2'>Filter by tags</Typography>
        <Stack
          gap={1}
          paddingY={1}
          flexWrap='wrap'
          direction="row"
        >
          <ToggleChip
            toggledColor={vyTheme.palette.common.black}
            toggledContrastColor={vyTheme.palette.common.white}
            initialToggle={filters?.includes(APPROVAL_FLOW_INVOICE_TYPES.OVERAGES)}
            onToggle={(toggleState) => handleFilterChange(APPROVAL_FLOW_INVOICE_TYPES.OVERAGES, toggleState)}
            label='Overages'
          />
          <ToggleChip
            toggledColor={vyTheme.palette.common.black}
            toggledContrastColor={vyTheme.palette.common.white}
            initialToggle={filters?.includes(APPROVAL_FLOW_INVOICE_TYPES.OLD_CYCLES)}
            onToggle={(toggleState) => handleFilterChange(APPROVAL_FLOW_INVOICE_TYPES.OLD_CYCLES, toggleState)}
            label='Old Invoices'
          />
          <ToggleChip
            toggledColor={vyTheme.palette.common.black}
            toggledContrastColor={vyTheme.palette.common.white}
            initialToggle={filters?.includes(APPROVAL_FLOW_INVOICE_TYPES.PREVIOUS_CYCLE)}
            onToggle={(toggleState) => handleFilterChange(APPROVAL_FLOW_INVOICE_TYPES.PREVIOUS_CYCLE, toggleState)}
            label='Current Cycle'
          />
        </Stack>
      </Box>
    </Stack>
  );
};

const MemoizedApprovalFlowInvoicesListHeader = React.memo(ApprovalFlowInvoicesListHeader);
export { MemoizedApprovalFlowInvoicesListHeader as ApprovalFlowInvoicesListHeader };

function useSelectedFilters() {
  return useStateParams<ApprovalFlowInvoiceTypes[]>([APPROVAL_FLOW_INVOICE_TYPES.PREVIOUS_CYCLE], 'filters', serializeStringArrayParam, deserializeEnumArrayParam(APPROVAL_FLOW_INVOICE_TYPES));
}